import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DataCard, SectionHeader } from "../components/UI";
import MonitoringWidget from "../components/MonitoringWidget";
import {
  Users, ScrollText, Sparkles, ClipboardList, ClipboardCheck, ArrowRight,
  Workflow as WorkflowIcon, CreditCard, BadgeCheck, LifeBuoy,
} from "lucide-react";

const ROLES = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR" },
  { value: "company_admin", label: "Company Admin" },
];

export default function CompanyAdmin() {
  const { tenant } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingPolicies, setPendingPolicies] = useState(0);
  const [pendingFindings, setPendingFindings] = useState(0);
  const [pendingArchives, setPendingArchives] = useState(0);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);

  useEffect(() => {
    api.get("/analytics/overview").then(r => setOverview(r.data)).catch(()=>{});
    api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
    api.get("/policies", { params: { status: "pending_approval" } }).then(r => setPendingPolicies(r.data.length)).catch(()=>{});
    api.get("/cases", { params: { limit: 500 } }).then(r => {
      setPendingFindings(r.data.filter(c => (c.findings)?.status === "pending_approval").length);
    }).catch(()=>{});
    api.get("/evidence-archive-requests", { params: { status: "pending" } }).then(r => setPendingArchives(r.data.length)).catch(()=>{});
    api.get("/tickets", { params: { status: "open" } }).then(r => setOpenTickets(r.data.length)).catch(()=>{});
  }, []);

  useEffect(() => {
    setProfileCompletion(tenant?.profile_completion ?? 0);
  }, [tenant]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <SectionHeader
        title="Company Administration"
        subtitle={`${tenant?.name || "Your workspace"} , governance, users, and compliance.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <DataCard label="Workforce" value={users.length} testid="admin-workforce" />
        <DataCard label="Total cases" value={overview?.total ?? ","} testid="admin-total" />
        <DataCard label="Open" value={overview?.open ?? ","} accent testid="admin-open" />
        <DataCard label="Avg TTR" value={overview ? `${overview.avg_resolution_hours}h` : ","} testid="admin-ttr" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Link to="/admin/approvals" className="data-cell p-6 hover:border-blue-600 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <ClipboardCheck size={18} className="text-blue-700" strokeWidth={1.75}/>
            <ArrowRight size={14} className="text-slate-400"/>
          </div>
          <div className="font-display text-2xl font-semibold text-slate-900">{pendingFindings + pendingPolicies + pendingArchives}</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">PENDING APPROVALS</div>
          <div className="mt-3 flex gap-3 text-[10px] font-mono text-slate-500">
            <span>{pendingFindings} findingsList</span>
            <span>{pendingPolicies} policies</span>
            <span>{pendingArchives} archives</span>
          </div>
        </Link>

        <MonitoringWidget />

        <Link to="/admin/company-profile" className="data-cell p-6 hover:border-blue-600 transition-colors" data-testid="admin-company-profile-card">
          <div className="flex items-center justify-between mb-3">
            <BadgeCheck size={18} className="text-blue-700" strokeWidth={1.75}/>
            <ArrowRight size={14} className="text-slate-400"/>
          </div>
          <div className="font-display text-2xl font-semibold text-slate-900">{profileCompletion}%</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">COMPANY PROFILE</div>
          <div className="mt-3 h-1.5 bg-slate-100">
            <div className="h-full bg-blue-600" style={{ width: `${profileCompletion}%` }}/>
          </div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Link to="/admin/billing" className="data-cell p-6 hover:border-blue-600 transition-colors" data-testid="admin-billing-card">
          <div className="flex items-center justify-between mb-3">
            <CreditCard size={18} className="text-blue-700" strokeWidth={1.75}/>
            <ArrowRight size={14} className="text-slate-400"/>
          </div>
          <div className="font-display text-2xl font-semibold text-slate-900 capitalize">{tenant?.plan || ","}</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">SUBSCRIPTION PLAN</div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <Link to="/admin/users" className="data-cell p-5 hover:border-blue-600 transition-colors">
          <Users size={16} className="text-slate-700 mb-2" strokeWidth={1.75}/>
          <div className="font-semibold text-slate-900">Users</div>
          <div className="text-xs text-slate-500 font-mono">{users.length} accounts</div>
        </Link>
        <Link to="/admin/resources" className="data-cell p-5 hover:border-blue-600 transition-colors">
          <ScrollText size={16} className="text-slate-700 mb-2" strokeWidth={1.75}/>
          <div className="font-semibold text-slate-900">Resources</div>
          <div className="text-xs text-slate-500 font-mono">WYSIWYG · approvals</div>
        </Link>
        <Link to="/admin/workflows" className="data-cell p-5 hover:border-blue-600 transition-colors" data-testid="admin-workflows-card">
          <WorkflowIcon size={16} className="text-slate-700 mb-2" strokeWidth={1.75}/>
          <div className="font-semibold text-slate-900">Workflows</div>
          <div className="text-xs text-slate-500 font-mono">Designer & templates</div>
        </Link>
        <Link to="/tickets" className="data-cell p-5 hover:border-blue-600 transition-colors" data-testid="admin-tickets-card">
          <LifeBuoy size={16} className="text-slate-700 mb-2" strokeWidth={1.75}/>
          <div className="font-semibold text-slate-900">Support</div>
          <div className="text-xs text-slate-500 font-mono">{openTickets} open ticket{openTickets === 1 ? "" : "s"}</div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="data-cell p-6">
          <div className="eyebrow mb-4">Roles Distribution</div>
          {ROLES.map((r) => {
            const count = users.filter((u) => u.role === r.value).length;
            const pct = users.length ? (count / users.length) * 100 : 0;
            return (
              <div key={r.value} className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-700">{r.label}</span>
                  <span className="font-mono text-slate-500">{count}</span>
                </div>
                <div className="h-1.5 bg-slate-100">
                  <div className="h-full bg-slate-900" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
        </div>

        <div className="ai-surface tracing-beam p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} strokeWidth={1.75} className="text-blue-400"/>
            <span className="eyebrow text-blue-300">Enterprise Intelligence</span>
          </div>
          <div className="font-editorial text-xl text-slate-100 leading-snug mb-5">
            "See the workplace as one system. PropHR AI synthesizes patterns across every case and points you to what matters."
          </div>
          <Link to="/admin/enterprise-intelligence" className="btn-ghost text-xs w-full">
            <Sparkles size={14} strokeWidth={2}/> Open Enterprise Intelligence <ArrowRight size={12} className="ml-auto"/>
          </Link>
        </div>
      </div>
    </div>
  );
}
